/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `index` for the Caintra Connect frontend.
 * @module modals/CategoryCreationModal/index
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import { logger, sanitizeError } from "../../utils/logger.js";
import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { refreshToken, signOut, getLoggedAuthToken } from "../../utils/common.js";
import { TOKEN_EXPIRED_MESSAGE } from "../../utils/apiConstants.js";
import styles from "./CategoryCreationModal.module.css";

const SUCCESS_MESSAGE_DURATION_MS = 3000;
const HTTP_CONFLICT = 409;

const CategoryCreationModal = ({ open, onClose, onCreateCategory }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [categoryName, setCategoryName] = useState("");
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [messageError, setMessageError] = useState("");
  const dialogRef = useRef(null);

  const handleCreateCategory = async () => {
    const categoryData = {
      nombre: categoryName,
    };

    const authToken = getLoggedAuthToken();

    if (!authToken) {
      logger.error("Failed to get the token");
      return;
    }

    try {
      const options = {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify(categoryData),
      };
      const response = await fetch(
        `${process.env.REACT_APP_ROOT_API}category`,
        options,
      );

      if (response.ok) {
        setMessageError("");
        const responseData = await response.json();
        const newCategory = responseData.category;
        onCreateCategory(newCategory);
        onClose();
        setShowSuccessMessage(true);
        setTimeout(() => {
          setShowSuccessMessage(false);
        }, SUCCESS_MESSAGE_DURATION_MS);
      } else {
        const errorData = await response.json();
        if (errorData?.message === TOKEN_EXPIRED_MESSAGE) {
          const newTokens = await refreshToken();
          logger.info("newTokens: ", { data: newTokens });
          if (!newTokens) {
            await signOut();
            navigate("/");
            return;
          }
          await handleCreateCategory();
          return;
        }
        if (response.status === HTTP_CONFLICT) {
          setMessageError(t("components.categoryCreationModal.categoryExistsError"));
        }
        logger.error("Failed to create category:", {
          data: [response.status, errorData.message],
        });
      }
    } catch (error) {
      logger.error("Error:", { err: sanitizeError(error) });
    }
  };

  useEffect(() => {
    const handleClose = () => {
      setMessageError("");
      onClose();
    };

    const dialog = dialogRef.current;
    if (dialog) {
      dialog.addEventListener("close", handleClose);
    }

    if (open) {
      setCategoryName("");
      dialog.showModal();
    } else {
      dialog.close();
    }

    return () => {
      if (dialog) {
        dialog.removeEventListener("close", handleClose);
      }
    };
  }, [open, onClose]);

  return (
    <>
      {open && <div className={styles.overlay}></div>}
      <dialog ref={dialogRef} className={styles.modal}>
        <div className={styles.closeButtonContainer}>
          <button className={styles.closeButton} onClick={onClose}>
            &times;
          </button>
        </div>

        <div className={styles.modalContent}>
          <label className={styles.modalTitle}>
            {t("components.categoryCreationModal.title")}
          </label>
          <div style={{}}>
            <label className={styles.modalLabel}>
              {t("components.categoryCreationModal.categoryLabel")}
            </label>
            <div className={styles.inputContainer}>
              <div className={styles.inputWrapper}>
                <input
                  type="text"
                  placeholder={t(
                    "components.categoryCreationModal.categoryPlaceholder",
                  )}
                  value={categoryName}
                  onChange={(e) => setCategoryName(e.target.value)}
                  className={styles.modalInput}
                />
                {categoryName && (
                  <button
                    className={styles.inputClearButton}
                    onClick={() => {
                      setMessageError("");
                      setCategoryName("");
                    }}
                  >
                    &times;
                  </button>
                )}
              </div>
            </div>
            {messageError && (
              <span className={styles.errorMessage}>{messageError}</span>
            )}
          </div>
        </div>

        <div className={styles.modalButtons}>
          <button onClick={onClose} className={styles.cancelButton}>
            {t("common.cancel")}
          </button>
          <button
            className={styles.createButton}
            onClick={handleCreateCategory}
            disabled={!categoryName}
          >
            {t("components.categoryCreationModal.createButton")}
          </button>
        </div>
      </dialog>
      {showSuccessMessage && (
        <div className={styles.successMessage}>
          {t("components.categoryCreationModal.successMessage")}
        </div>
      )}
    </>
  );
};

export default CategoryCreationModal;
